import type { ColumnDef } from '@tanstack/react-table';
import { Download, Search } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { organizationService } from '@/feature/organization/services/organizationService';
import type { IOrganization } from '@/feature/organization/types/organization';
import { ROUTES } from '@/routes/routeConstants';
import { TanStackTable } from '@/shared/components/data-display/TanStackTable';
import SearchableSelect from '@/shared/components/forms/SearchableSelect';
import Button from '@/shared/components/ui/Button';
import Tabs from '@/shared/components/ui/Tabs';

import { auditTrailService } from './services/auditTrailService';
import type { AuditLogScope, IAuditLogEntry } from './types/auditTrail';

function safeString(v: unknown): string {
  if (typeof v === 'string') {
    return v;
  }
  if (v === null || v === undefined) {
    return '';
  }
  return String(v);
}

function formatDateTime(value: string | number | Date): { date: string; time: string } {
  const d = value instanceof Date ? value : new Date(value);
  if (!Number.isFinite(d.getTime())) {
    return { date: '—', time: '' };
  }
  const date = d.toISOString().split('T')[0] || '—';
  const time = d.toTimeString().slice(0, 8);
  return { date, time };
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return '—';
  }
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  const out = `${first}${last}`.toUpperCase();
  return out.trim() === '' ? '—' : out;
}

function toCsvValue(v: unknown): string {
  const s = safeString(v);
  // CSV escaping
  if (/[,"\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function downloadCsv(filename: string, rows: Record<string, unknown>[]) {
  const headers = Array.from(
    rows.reduce((set, r) => {
      Object.keys(r).forEach((k) => set.add(k));
      return set;
    }, new Set<string>())
  );

  const csv = [
    headers.map(toCsvValue).join(','),
    ...rows.map((r) => headers.map((h) => toCsvValue(r[h])).join(',')),
  ].join('\n');

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

type TimeFilterKey = 'all' | 'today' | 'last_7_days' | 'last_30_days';

const AuditTrailPage = () => {
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState<IAuditLogEntry[]>([]);
  const [organizations, setOrganizations] = useState<IOrganization[]>([]);

  const [activeTab, setActiveTab] = useState(0);
  const scope: AuditLogScope = activeTab === 0 ? 'admin' : 'app';

  const [search, setSearch] = useState('');
  const [orgFilter, setOrgFilter] = useState<string>('all');
  const [eventTypeFilter, setEventTypeFilter] = useState<string>('all');
  const [timeFilter, setTimeFilter] = useState<TimeFilterKey>('all');

  const fetchOrgs = useCallback(async () => {
    const orgs = await organizationService.getOrganizations();
    setOrganizations(orgs);
  }, []);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const data = await auditTrailService.getAuditLogs({
        scope,
      });
      setLogs(data);
    } finally {
      setLoading(false);
    }
  }, [scope]);

  useEffect(() => {
    fetchOrgs();
  }, [fetchOrgs]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const orgOptions = useMemo(() => {
    const opts = organizations
      .map((o) => {
        const anyOrg = o as unknown as Record<string, unknown>;
        const id = safeString(anyOrg.id);
        const name =
          safeString(anyOrg.org_name) ||
          safeString(anyOrg.organizationName) ||
          safeString(anyOrg.name);
        return { value: id || name, label: name || id || '—' };
      })
      .filter((o) => o.label !== '—');
    opts.sort((a, b) => a.label.localeCompare(b.label));
    return [{ value: 'all', label: 'All Organizations' }, ...opts];
  }, [organizations]);

  const derivedEventTypes = useMemo(() => {
    const set = new Set<string>();
    logs.forEach((l) => {
      if (l.eventType && l.eventType.trim() !== '') {
        set.add(l.eventType.trim());
      }
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [logs]);

  const eventTypeOptions = useMemo(
    () => [
      { value: 'all', label: 'All Event Types' },
      ...derivedEventTypes.map((t) => ({ value: t, label: t })),
    ],
    [derivedEventTypes]
  );

  const timeOptions = useMemo(
    () => [
      { value: 'all', label: 'All Time' },
      { value: 'today', label: 'Today' },
      { value: 'last_7_days', label: 'Last 7 days' },
      { value: 'last_30_days', label: 'Last 30 days' },
    ],
    []
  );

  const filteredLogs = useMemo(() => {
    const q = search.trim().toLowerCase();
    const now = new Date();
    const startOfToday = new Date(now);
    startOfToday.setHours(0, 0, 0, 0);
    const startOfLast7 = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const startOfLast30 = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

    return logs
      .filter((l) => {
        if (orgFilter !== 'all') {
          const orgIdOrName =
            (l.organizationId && l.organizationId.trim() !== ''
              ? l.organizationId
              : l.organizationName) ?? '';
          if (orgIdOrName !== orgFilter) {
            return false;
          }
        }
        if (eventTypeFilter !== 'all' && l.eventType !== eventTypeFilter) {
          return false;
        }
        if (timeFilter !== 'all') {
          const t = new Date(l.timestamp);
          if (!Number.isFinite(t.getTime())) {
            return false;
          }
          if (timeFilter === 'today' && t < startOfToday) {
            return false;
          }
          if (timeFilter === 'last_7_days' && t < startOfLast7) {
            return false;
          }
          if (timeFilter === 'last_30_days' && t < startOfLast30) {
            return false;
          }
        }
        if (q === '') {
          return true;
        }
        const hay = [
          l.eventTitle,
          l.eventDescription,
          l.organizationName,
          l.performedByName,
          l.performedByRole,
          l.category,
          l.status,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return hay.includes(q);
      })
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  }, [logs, orgFilter, eventTypeFilter, timeFilter, search]);

  const columns = useMemo<ColumnDef<IAuditLogEntry>[]>(
    () => [
      {
        accessorKey: 'eventTitle',
        header: 'Event',
        cell: (info) => {
          const r = info.row.original;
          return (
            <div className='min-w-0'>
              <div className='font-semibold text-gray-900 truncate'>{r.eventTitle || '—'}</div>
              {r.eventDescription ? (
                <div className='text-sm text-gray-500 truncate'>{r.eventDescription}</div>
              ) : null}
            </div>
          );
        },
        meta: { width: '28%', noTruncate: true },
      },
      {
        accessorKey: 'organizationName',
        header: 'Organization',
        cell: (info) => <span className='text-gray-900'>{(info.getValue() as string) || '—'}</span>,
      },
      {
        id: 'performedBy',
        header: 'Performed By',
        cell: (info) => {
          const r = info.row.original;
          const name = r.performedByName || '—';
          const role = r.performedByRole || '';
          return (
            <div className='flex items-center gap-3 min-w-0'>
              <div className='h-9 w-9 shrink-0 rounded-full bg-theme-100 text-theme-700 flex items-center justify-center font-bold text-sm'>
                {initials(name)}
              </div>
              <div className='min-w-0'>
                <div className='font-semibold text-gray-900 truncate' title={name}>
                  {name}
                </div>
                {role ? <div className='text-xs text-gray-500 truncate'>{role}</div> : null}
              </div>
            </div>
          );
        },
        meta: { width: '16%', noTruncate: true },
      },
      {
        accessorKey: 'category',
        header: 'Category',
        cell: (info) => <span className='text-gray-900'>{(info.getValue() as string) || '—'}</span>,
      },
      {
        accessorKey: 'timestamp',
        header: 'Timestamp',
        cell: (info) => {
          const { date, time } = formatDateTime(info.getValue() as string);
          return (
            <div className='min-w-0'>
              <div className='text-gray-900 font-medium'>{date}</div>
              {time ? <div className='text-xs text-gray-500'>{time}</div> : null}
            </div>
          );
        },
        meta: { width: '14%', noTruncate: true },
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: (info) => {
          const v = safeString(info.getValue()).toLowerCase();
          const ok = v === 'success' || v === 'succeeded' || v === 'ok';
          let label = '—';
          if (ok) {
            label = 'Success';
          } else if (v) {
            label = v[0]?.toUpperCase() + v.slice(1);
          }
          return (
            <span
              className={[
                'inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold',
                ok ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700',
              ].join(' ')}
            >
              {label}
            </span>
          );
        },
        meta: { width: '10%', noTruncate: true },
      },
    ],
    []
  );

  const handleExport = useCallback(() => {
    const rows = filteredLogs.map((l) => ({
      scope: l.scope,
      event: l.eventTitle,
      event_description: l.eventDescription,
      event_type: l.eventType,
      organization: l.organizationName,
      organization_id: l.organizationId,
      performed_by: l.performedByName,
      performed_by_role: l.performedByRole,
      category: l.category,
      timestamp: l.timestamp,
      status: l.status,
    }));
    const ts = new Date().toISOString().replace(/[:.]/g, '-');
    downloadCsv(`audit-trail-${scope}-${ts}.csv`, rows);
  }, [filteredLogs, scope]);

  const cardHeader = useMemo(
    () => (
      <div className='flex flex-col gap-3'>
        <div className='flex items-start justify-between gap-4 flex-wrap'>
          <div className='min-w-0'>
            <h2 className='text-lg sm:text-xl font-bold text-gray-900 m-0'>
              {scope === 'admin' ? 'Admin Portal Audit Logs' : 'Application Audit Logs'}
            </h2>
            <p className='mt-1 text-sm text-gray-500 mb-0'>
              System-level events performed by super admins across all organizations
            </p>
          </div>
          <div className='min-w-[260px]'>
            <div className='text-xs text-gray-500 font-semibold mb-1'>Switch view</div>
            <Tabs
              activeTabIndex={activeTab}
              onTabChange={(i) => setActiveTab(i)}
              className='w-full!'
              hideContent
              options={[
                { label: 'Admin Portal', content: null },
                { label: 'HUMIT Application', content: null },
              ]}
            />
          </div>
        </div>
        <div className='flex flex-wrap items-center gap-3 w-full'>
          <div className='w-full min-w-0 sm:flex-1 sm:min-w-[320px]'>
            <div className='relative'>
              <Search className='absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4' />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder='Search events, users, organizations...'
                className='w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg bg-white text-gray-800 shadow-sm focus:outline-none focus:ring-2 focus:ring-theme-500 focus:border-transparent transition-all duration-200'
              />
            </div>
          </div>
          <div className='w-full min-w-0 sm:w-56'>
            <SearchableSelect
              value={orgFilter}
              onChange={(v) => setOrgFilter(v)}
              options={orgOptions}
              placeholder='All Organizations'
              className='rounded-lg! py-2! border! border-gray-200!'
              containerClassName='!space-y-0'
            />
          </div>
          <div className='w-full min-w-0 sm:w-48'>
            <SearchableSelect
              value={eventTypeFilter}
              onChange={(v) => setEventTypeFilter(v)}
              options={eventTypeOptions}
              placeholder='All Event Types'
              className='rounded-lg! py-2! border! border-gray-200!'
              containerClassName='!space-y-0'
            />
          </div>
          <div className='w-full min-w-0 sm:w-40'>
            <SearchableSelect
              value={timeFilter}
              onChange={(v) => setTimeFilter(v as TimeFilterKey)}
              options={timeOptions}
              placeholder='All Time'
              className='rounded-lg! py-2! border! border-gray-200!'
              containerClassName='!space-y-0'
            />
          </div>
          <div className='w-full min-w-0 sm:w-auto sm:ml-auto'>
            <Button
              type='button'
              variant='outline'
              className='rounded-lg! px-4! py-2!'
              onClick={handleExport}
            >
              <Download className='w-4 h-4 mr-2' />
              Export CSV
            </Button>
          </div>
        </div>
      </div>
    ),
    [
      scope,
      activeTab,
      search,
      orgFilter,
      orgOptions,
      eventTypeFilter,
      eventTypeOptions,
      timeFilter,
      timeOptions,
      handleExport,
    ]
  );

  return (
    <div className='px-6 pt-3 pb-6 bg-gray-50 min-h-[calc(100vh-4rem)]'>
      <nav className='flex items-center gap-2 mb-4 text-sm'>
        <Link
          to={ROUTES.DASHBOARD}
          className='flex items-center text-theme-500 hover:opacity-80 transition-opacity [&_svg]:stroke-theme-500'
        >
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='16'
            height='16'
            viewBox='0 0 24 24'
            fill='none'
            stroke='currentColor'
            strokeWidth='2'
            strokeLinecap='round'
            strokeLinejoin='round'
            aria-hidden='true'
          >
            <path d='M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8'></path>
            <path d='M3 10a2 2 0 0 1 .709-1.528l7-5.999a2 2 0 0 1 2.582 0l7 5.999A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z'></path>
          </svg>
        </Link>
        <span className='text-gray-400 text-base'>›</span>
        <span className='text-gray-500 font-normal'>Audit Trail</span>
      </nav>

      <TanStackTable<IAuditLogEntry>
        data={filteredLogs}
        columns={columns}
        loading={loading}
        emptyMessage='No audit logs found.'
        enableSearch={false}
        cardHeader={cardHeader}
        minHeight='0'
        pageSize={10}
        getRowId={(r) => r.id}
        resetStateOnDataChange={true}
        manualSorting={true}
      />
    </div>
  );
};

export default AuditTrailPage;
